import './BaseReport.scss';

import React from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import LogoSpinner from 'src/component/UI/LogoSpinner';

interface BaseReportProps {
  title: string;
  data: any[];
  loading: boolean;
  columns: any[];
  customToolbar?: React.ComponentType;
  getRowId?: (row: any) => string | number;
  noDataMessage?: string;
  hasRun?: boolean;
}

/**
 * Base report component that provides common structure for all reports
 * Handles loading spinner, Paper wrapper, title, and DataGrid setup
 */
export default function BaseReport ({
  title,
  data,
  loading,
  columns,
  customToolbar,
  getRowId,
  noDataMessage,
  hasRun = false
}: BaseReportProps) {
  if (loading) {
    return (
      <div className="loading-container">
        <LogoSpinner size={200} />
      </div>
    );
  }

  if (!data || data.length === 0) {
    // Only show no data message if report has been run
    if (noDataMessage && hasRun) {
      return (
        <Paper className="report-paper">
          <h2>{title}</h2>
          <Box sx={{ padding: '40px', textAlign: 'center' }}>
            {noDataMessage}
          </Box>
        </Paper>
      );
    }
    return null;
  }

  return (
    <Paper className="report-paper">
      <h2>{title}</h2>

      <Box className="report-data-grid-container">
        <DataGrid
          rows={data}
          columns={columns}
          {...(getRowId && { getRowId })}
          sx={{
            border: 'none', // Removes the outer border
            '& .MuiDataGrid-columnSeparator': {
              display: 'none', // Removes the separator lines between column headers
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
            },
          }}
          density='compact'
          components={{
            Toolbar: customToolbar,
          }}
        />
      </Box>
    </Paper>
  );
}

BaseReport.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  columns: PropTypes.array.isRequired,
  customToolbar: PropTypes.elementType,
  getRowId: PropTypes.func,
  noDataMessage: PropTypes.string,
  hasRun: PropTypes.bool
};
